// --- Almacenamiento Básico en IndexedDB (Capa de Acceso Directo) ---
import { SECRET_PASSPHRASE } from '@constants/secret'
import { decryptData, encryptData, isWebCryptoAvailable } from '@security/crypto'
import { get, set, del, createStore } from 'idb-keyval'
import { compressData, decompressData } from './compressed' // Importar funciones de compresión

/**
 * @private
 * @const {IDBStore} portfolioIDBStore
 * @description Instancia de la tienda IndexedDB personalizada creada con `idb-keyval`.
 * Esta tienda es utilizada internamente por `rawIndexedDBStorage` para todas las operaciones de base de datos.
 * El nombre de la base de datos y de la tienda se configuran aquí.
 */
const portfolioIDBStore = createStore(
  'alejandro@millahue/database', // Nombre de la base de datos
  'portafolio@millahue/store' // Nombre de la tienda dentro de la base de datos
)
/**
 * @object rawIndexedDBStorage
 * @description Proporciona una capa de abstracción delgada y directa para interactuar con IndexedDB
 * a través de la librería `idb-keyval`. Este objeto NO realiza cifrado, descifrado, compresión o descompresión.
 * Simplemente guarda y recupera los valores tal como se los pasa la capa superior (secureIndexedDBStorage).
 * @private
 */
const rawIndexedDBStorage = {
  /**
   * @method getItem
   * @memberof rawIndexedDBStorage
   * @description Recupera un valor de IndexedDB asociado con la clave especificada.
   * @async
   * @param {string} name - La clave del elemento que se desea recuperar.
   * @returns {Promise<any|null>} Una promesa que se resuelve con el valor almacenado
   * (se espera que sea un string encriptado o null), o `null` si la clave no se encuentra.
   */
  getItem: async name => {
    try {
      const value = await get(name, portfolioIDBStore)
      return value === undefined ? null : value
    } catch (error) {
      console.error(`Error obteniendo el elemento '${name}' de rawIndexedDBStorage:`, error)
      return null
    }
  },

  /**
   * @method setItem
   * @memberof rawIndexedDBStorage
   * @description Almacena un par clave-valor en IndexedDB.
   * @async
   * @param {string} name - La clave bajo la cual se almacenará el valor.
   * @param {any} value - El valor que se desea almacenar (se espera que sea un string encriptado o null).
   * @returns {Promise<void>} Una promesa que se resuelve una vez que la operación de guardado se ha completado.
   */
  setItem: async (name, value) => {
    try {
      await set(name, value, portfolioIDBStore)
    } catch (error) {
      console.error(`Error estableciendo el elemento '${name}' en rawIndexedDBStorage:`, error)
    }
  },

  /**
   * @method removeItem
   * @memberof rawIndexedDBStorage
   * @description Elimina un elemento de IndexedDB identificado por su clave.
   * @async
   * @param {string} name - La clave del elemento que se desea eliminar.
   * @returns {Promise<void>} Una promesa que se resuelve una vez que la operación de eliminación se ha completado.
   * No se rechaza en caso de error, pero registra el error en la consola.
   */
  removeItem: async name => {
    if (typeof window === 'undefined') return
    try {
      await del(name, portfolioIDBStore)
    } catch (error) {
      console.error(`Error eliminando el elemento '${name}' de rawIndexedDBStorage:`, error)
    }
  }
}

// --- Almacenamiento Seguro en IndexedDB (Capa de Servicio con Cifrado y Compresión) ---

/**
 * @object secureIndexedDBStorage
 * @description Proporciona una interfaz pública para interactuar de forma segura y eficiente con IndexedDB.
 * Esta capa se encarga de:
 * 1. Comprimir los datos en texto plano (usando `compressData`).
 * 2. Cifrar los datos comprimidos (usando `encryptData`).
 * 3. Almacenar los datos cifrados y comprimidos (usando `rawIndexedDBStorage`).
 * Y al recuperar:
 * 1. Obtener los datos cifrados y comprimidos.
 * 2. Descifrarlos (usando `decryptData`) para obtener los datos comprimidos.
 * 3. Descomprimirlos (usando `decompressData`) para obtener el texto plano original.
 * La práctica general recomendada es comprimir los datos ANTES de encriptarlos.
 * @abstract
 * - Efectividad de la Compresión: Los algoritmos de compresión buscan patrones y redundancias. Los datos encriptados están diseñados para parecerse a ruido aleatorio, por lo que tienen muy poca redundancia y se comprimen muy mal (o incluso pueden aumentar ligeramente de tamaño). Comprimir el texto plano original primero suele ser mucho más efectivo.
 * - Seguridad (Potencial Fuga de Información): Si comprimes después de encriptar, el tamaño final del archivo comprimido podría revelar información sobre la naturaleza de los datos originales sin encriptar (por ejemplo, su compresibilidad). Comprimir primero ayuda a mitigar este tipo de análisis.
 * - Eficiencia: Encriptar datos más pequeños (ya comprimidos) es generalmente más rápido.
 * Por lo tanto, el flujo ideal es:
 * 1. GUARDAR: Datos Originales -> Comprimir -> Encriptar -> Almacenar
 * 2. OBTENER: Recuperar -> Desencriptar -> Descomprimir -> Datos Originales
 */
export const secureIndexedDBStorage = {
  /**
   * @method setItem
   * @memberof secureIndexedDBStorage
   * @description Comprime, luego cifra un valor en texto plano y lo almacena en IndexedDB.
   * @async
   * @param {string} name - La clave única para el elemento.
   * @param {any} plainValue - El valor en texto plano a almacenar.
   * @returns {Promise<void>}
   */
  setItem: async (name, plainValue) => {
    if (!isWebCryptoAvailable()) return

    try {
      // 1. Comprimir el valor en texto plano
      const compressedValue = compressData(plainValue) // Devuelve Uint8Array o null

      if (compressedValue === null && plainValue !== null && typeof plainValue !== 'undefined') {
        console.error(`Fallo al comprimir el elemento '${name}'. No se guardó.`)
        return
      }

      const valueToEncrypt = compressedValue === null ? plainValue : compressedValue
      const encryptedData = await encryptData(valueToEncrypt, SECRET_PASSPHRASE)

      if (encryptedData) {
        await rawIndexedDBStorage.setItem(name, encryptedData)
      } else {
        console.error(`Fallo al encriptar el elemento '${name}'. No se guardó.`)
      }
    } catch (error) {
      console.error(`Error en secureIndexedDBStorage.setItem para la clave '${name}':`, error)
    }
  },

  /**
   * @method getItem
   * @memberof secureIndexedDBStorage
   * @description Recupera un elemento, lo descifra y luego lo descomprime.
   * @async
   * @param {string} name - La clave del elemento.
   * @returns {Promise<any|null>} El valor original en texto plano, o null.
   */
  getItem: async name => {
    if (!isWebCryptoAvailable()) return null

    try {
      const encryptedData = await rawIndexedDBStorage.getItem(name)

      if (encryptedData === null) return null

      const decryptedCompressedData = await decryptData(encryptedData, SECRET_PASSPHRASE)

      if (decryptedCompressedData === null) return null

      if (decryptedCompressedData instanceof Uint8Array) {
        return decompressData(decryptedCompressedData)
      } else {
        return decryptedCompressedData
      }
    } catch (error) {
      console.error(`Error en secureIndexedDBStorage.getItem para la clave '${name}':`, error)
      return null
    }
  },

  /**
   * @method removeItem
   * @memberof secureIndexedDBStorage
   * @description Elimina un elemento (cifrado o no, ya que opera sobre la clave) de IndexedDB.
   * Esta operación no requiere acceso a la Web Crypto API ya que no implica descifrado.
   * @async
   * @param {string} name - La clave del elemento que se desea eliminar.
   * @returns {Promise<void>} Una promesa que se resuelve una vez que la operación de eliminación se ha completado.
   */
  removeItem: async name => {
    try {
      await rawIndexedDBStorage.removeItem(name)
    } catch (error) {
      console.error(`Error en secureIndexedDBStorage.removeItem para la clave '${name}':`, error)
    }
  }
}

// Exportar el almacenamiento seguro para IndexedDB como la interfaz principal del módulo.
export default secureIndexedDBStorage
